import React, { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import classNames from 'classnames'
import { T, includes } from 'ramda'
import OnVisible from 'react-on-visible'
import { NavLink } from 'react-router-dom'
import ReactSVG from 'react-svg'

import Clamped from '../shared/clamped'
import { getLocationBaseWithHref } from '../../lib/location'
import {
  getResponsiveTileShowCount,
  getResponsiveSecondaryHeroTileShowCount,
  truncateListNameText
} from '../../lib/utils'

import {
  THEME_OPTIONS,
  STATIC_SCREENS,
  BROWSE_RENTALS,
  CAROUSEL_TILE_LIMIT
} from '../../constants'

import CarouselTile from './tile'
import MyRentalsTile from './tile/my-rentals'
import ContinuityTile from './tile/continuity-tile'
import TopTenTile from './tile/top-ten'
import SecondaryHeroTile from './tile/secondary-hero'
import CarouselEmptyMyRentals from './carousel-empty-my-rentals'
import NavLinkWithQuery from '../shared/nav-link-with-query'
import styles from './carousel.css'

import CAROUSEL_COMPONENT_FRAGMENT from '../../../graphql/fragments/carousel-component.gql'
import { JSONLD } from '../jsonld/index'
import { createItemListLDSchema } from '../../lib/jsonld/item-list'
import { IdleQueue } from '../../lib/idlize/IdleQueue.mjs'
import { ContinuityContext } from '../../modules/continuity/hooks/continuity-context'
import { GET_ALL_TILES } from '../../modules/continuity/hooks/continuity-reducer'
import Button from '../button'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import { useScreenWidth } from '../../hooks/use-screen-width'
import arrowRight from '../../../images/arrow-right-light.svg'
import episodesIcon from '../../../images/neon/episodes.svg'
import EmblaCarousel from '../emblaCarousel'

const getTileType = (carousel) => {
  if (carousel.subType === STATIC_SCREENS.MY_RENTALS.subType) {
    return MyRentalsTile
  }
  if (carousel.subType === 'MY_CONTINUE_WATCHING') {
    return ContinuityTile
  }
  if (carousel.subType === 'TOP_TEN') {
    return TopTenTile
  }
  if (carousel.subType === 'SECONDARY_HERO') {
    return SecondaryHeroTile
  }
  return CarouselTile
}

const getEmptyCarousel = (carousel, theme) => {
  return carousel.subType === STATIC_SCREENS.MY_RENTALS.subType ? (
    <CarouselEmptyMyRentals theme={theme} />
  ) : null
}

const carouselIdleQueue = new IdleQueue({
  defaultMinTaskTime: 5
})

const Carousel = React.memo(({
  carousel,
  theme,
  addToMyList,
  playbackInfoMany,
  isMarketing,
  screenTitle,
  inFeaturedTab
}) => {
  const isAuthenticated = useIsAuthenticated()
  const screenWidth = useScreenWidth()
  const [isVisible, setIsVisible] = useState(false)
  const setVisibility = (visible) => {
    if (!visible || visible === isVisible) {
      return
    }

    setIsVisible(true)
  }
  const [{ tiles: continuityTiles }, dispatch] = useContext(ContinuityContext)
  const { tiles, watchlistScreenId } = carousel

  useEffect(() => {
    carouselIdleQueue.pushTask(() => setVisibility(true))

    // Send all continue items to ContinuityContext hook for filter purpose
    if (carousel.subType === 'MY_CONTINUE_WATCHING' && tiles) {
      // set each continue item initial undo state
      const continueTiles = tiles.map(tile => ({ ...tile, isInUndoState: false }))
      dispatch({ type: GET_ALL_TILES, tiles: continueTiles })
    }

    return () => {
      carouselIdleQueue.clearPendingTasks()
    }
  }, [])

  if (!isVisible) {
    return <OnVisible onChange={visible => setVisibility(visible)} />
  }

  const isAllContentTVOD = carousel.tiles && carousel.tiles.length && carousel.tiles.every(tile => tile.contentItem.isRental)
  const isLoggedOutSecondaryHeroRentals = carousel.subType === 'SECONDARY_HERO' && isAllContentTVOD && !isAuthenticated

  let tilesLimit = tiles
  if (carousel.subType === 'MY_CONTINUE_WATCHING') {
    tilesLimit = continuityTiles
  } else if (carousel.subType === 'TOP_TEN') {
    tilesLimit = tiles.slice(0, CAROUSEL_TILE_LIMIT.TOP_TEN)
  } else if (isLoggedOutSecondaryHeroRentals) {
    tilesLimit = tiles.slice(0, CAROUSEL_TILE_LIMIT.SECONDARY_HERO)
  }
  const hasSlides = (tilesLimit || []).length > 0

  const emptyCarousel = getEmptyCarousel(carousel, theme)
  // Display carousel title when watchlistScreenId exists even there is no tile.
  if (!hasSlides && !emptyCarousel && !watchlistScreenId) {
    return null
  }
  const contentTypeClass =
    carousel.contentType === 'TITLE'
      ? styles.movieCarousel
      : styles.seriesCarousel

  /**
   * Tiles in Top Ten are wider than normal tiles due to the number in front of the image
   * So the number of displaying Top Ten tiles will be showCount - 1, and min number is 2
   * Tiles in BRAND are circular images which are smaller than normal tiles.
   * So the number of displaying BRAND tiles should be more than normal tiles.
   */
  const showCount = getResponsiveTileShowCount(screenWidth, 2)
  let slidesToShow = showCount
  if (carousel.subType === 'TOP_TEN' && showCount > 2) {
    slidesToShow = showCount - 1
  }

  if (carousel.subType === 'BRAND') {
    slidesToShow = showCount > 3 ? showCount + 2 : showCount + 1
  }

  if (carousel.subType === 'SECONDARY_HERO') {
    slidesToShow = getResponsiveSecondaryHeroTileShowCount(screenWidth, 1)
  }

  const isMyRentalsKids =
    carousel.subType === STATIC_SCREENS.MY_RENTALS.subType &&
    theme === THEME_OPTIONS.light

  const TileComponent = getTileType(carousel)

  return (
    <div
      data-lbx-e2e="logged-in-carousel"
      className={classNames(
        styles.carouselComponentWrapper,
        contentTypeClass,
        {
          [styles.whiteTheme]: isMarketing,
          [styles.seriesYMALWrapper]: isYMALSeriesPage(carousel),
          [styles.inFeaturedTab]: inFeaturedTab
        }
      )}
    >
      {!inFeaturedTab && (
        <div className={classNames(styles.carouselHeaderContainer, {
          [styles.inWatchlistScreen]: watchlistScreenId
        })}
        >
          {carousel.header && (
            <>
              <h2 className={styles.carouselHeader}>
                <Clamped
                  lines={2}
                  className={
                    classNames({
                      [styles.oneline]: carousel.subType === 'SEARCH'
                    })
                  }
                >
                  {watchlistScreenId ? truncateListNameText(carousel.header, 240, screenWidth) : carousel.header}
                </Clamped>
                {carousel.subType === 'SEARCH' && (
                  <span className={styles.headerCount}>
                    (
                    {carousel.tiles.length}
                    )
                  </span>
                )}
                {// Add line after carousel title
                  carousel.subType === STATIC_SCREENS.MY_RENTALS.subType &&
                  screenTitle === STATIC_SCREENS.MY_RENTALS.title && (
                    <span
                      className={classNames(styles.separator, {
                        [styles.kidsSeparator]: isMyRentalsKids
                      })}
                    />
                  )
                }
              </h2>

              { watchlistScreenId && (
                <NavLink to={watchlistScreenId} className={styles.viewDetailsLink}>
                  <Button
                    darkBGSecondary
                  >
                    <div className={styles.iconButton}>
                      View Details
                      <ReactSVG
                        src={arrowRight}
                        beforeInjection={(svg) => {
                          svg.setAttribute('class', styles.icon)
                        }}
                      />
                    </div>
                  </Button>
                </NavLink>
              )}
            </>
          )}
          {// Show the sponsor header below the carousel header on small screens (splitting to two lines) - breakpoint 768px
            (carousel.sponsorSupportText && carousel.sponsorLogoUrl && carousel.sponsorLogoText) && (
              <div className={styles.carouselSponsorHeader}>
                <div className={styles.carouselSponsorText}>{carousel.sponsorSupportText}</div>
                {// Show logo text instead of logo image on small screens - breakpoint 768px
                  <>
                    <img className={styles.carouselSponsorLogoImage} src={carousel.sponsorLogoUrl} alt="Sponsor logo" />
                    <span className={styles.carouselSponsorLogoText}>{carousel.sponsorLogoText}</span>
                  </>
                }
              </div>
            )
          }
        </div>
      )}

      { /* Display a placeholder section for lists in my lists page when there are no slides. */ }
      {!hasSlides && watchlistScreenId && (
        <div className={styles.emptyCarouselInMyList}>
          <ReactSVG
            src={episodesIcon}
            beforeInjection={(svg) => {
              svg.setAttribute('class', styles.episodesIcon)
            }}
          />
          <div className={styles.carouselHeader}>
            You have nothing here yet!
          </div>
          <div className={styles.emptyText}>
            Explore more content and add to this list to easily find them here.
          </div>
        </div>
      )}

      {hasSlides ? (
        <EmblaCarousel
          slidesToShow={slidesToShow}
          activeSlideCount={tilesLimit.length}
          emblaButtonsCustomStyle={carousel.subType === 'MY_CONTINUE_WATCHING' ? styles.isMyContinueWatching : ''}
        >
          {tilesLimit.map((tile, index) => (
            <div
              className={classNames(styles.emblaSlide, {
                [styles.isSecondaryHeroSlide]: carousel.subType === 'SECONDARY_HERO',
                [styles.isTopTenSlide]: carousel.subType === 'TOP_TEN',
                [styles.isBrandSlide]: carousel.subType === 'BRAND'
              })}
              key={tile.contentItem?.id || tile.id || index}
            >
              <TileComponent
                tile={tile}
                subType={carousel.subType}
                contentType={carousel.contentType}
                index={index}
                theme={theme}
                listTitle={carousel.header || ''}
                isOnMyList={false}
                addToMyList={addToMyList}
                playbackInfoMany={playbackInfoMany}
              />
            </div>
          ))}
        </EmblaCarousel>
      ) : emptyCarousel}

      {isLoggedOutSecondaryHeroRentals && !inFeaturedTab && (
        <>
          <div className={classNames(styles.carouselHeaderContainer, styles.carouselHeader)}>
            <p className={styles.rentalsInfo}>Access new release movies from the comfort of your home. Just pick a movie, rent it, and watch instantly – no need to subscribe!</p>
            <p className={styles.rentalsPrice}>Movie rental prices vary from $7.99 for New Release movies, $5.99 for Library movies and from $20.99 for Premium movies.</p>
          </div>
          <div className={styles.buttonsWrapper}>
            <NavLinkWithQuery to={BROWSE_RENTALS.url}>
              <Button
                text="Browse new rentals"
                className={styles.button}
                isRental
              />
            </NavLinkWithQuery>
          </div>
        </>
      )}
      <JSONLD
        schema={createItemListLDSchema(carousel.tiles)}
        type="ItemList"
      />
    </div>
  )
})

Carousel.propTypes = {
  carousel: propType(CAROUSEL_COMPONENT_FRAGMENT).isRequired,
  theme: PropTypes.oneOf([THEME_OPTIONS.dark, THEME_OPTIONS.light]).isRequired,
  addToMyList: PropTypes.func,
  playbackInfoMany: PropTypes.oneOfType([PropTypes.object]),
  isMarketing: PropTypes.bool,
  screenTitle: PropTypes.string,
  inFeaturedTab: PropTypes.bool
}

Carousel.defaultProps = {
  addToMyList: T,
  playbackInfoMany: null,
  isMarketing: false,
  screenTitle: null,
  inFeaturedTab: false
}

export default Carousel

export const isYMALSeriesPage = (carousel) => {
  return (includes('/series/', getLocationBaseWithHref()) || includes('/movie/', getLocationBaseWithHref())) && includes('You May Also Like', carousel.header)
}
